/* One day, hour by hour: where the audio is and what can be played.
 *
 * Three states, drawn as three different things because they are three
 * different facts:
 *
 *   in storage   durable, playable now, still here next year
 *   on the node  on an SD card behind a farm uplink, about a day deep, being
 *                trimmed as you look at it, and unreachable from here
 *   gap          nothing was captured
 *
 * The gap is drawn rather than left blank. R5.2 computes availability from what
 * actually arrived, and rule 4 is that a gap is recorded and never hidden -- an
 * empty stretch that looks like unrendered background is exactly the hiding
 * that rule forbids.
 *
 * There is deliberately no playhead. A scrubber says "any moment can be
 * played", and on this fleet that is false: `audio` uploads only at full tier
 * and events is the default, so the only playable things on most days are the
 * clips someone chose to save. Clip markers are buttons; empty time is not
 * clickable, because there is nothing behind it to reach.
 */
import { fmtTime } from '../api'
import { hourFraction } from '../time'

export interface DayHour {
  h: number
  node_seconds: number
  node_uploaded_seconds: number
  cloud_seconds: number
}

export interface DayClip {
  clip_id: string
  /** Null on a mark, which is a span of the day's own recording and not a
   *  file of its own. Only a live clip has an object to point at. */
  key: string | null
  started_utc: string
  duration_s: number
  /** Null on a mark. Nothing was captured, so there is no capture gain -- and
   *  a zero here would state that it was captured at unity, which is a
   *  measurement rather than an absence of one. */
  gain_db: number | null
  created_by: string
  created_by_name: string | null
  size: number | null
}

const HOUR = 3600

function pct(n: number): string {
  return `${Math.max(0, Math.min(100, n * 100))}%`
}

function label(h: DayHour | undefined, hour: number): string {
  const hh = `${String(hour).padStart(2, '0')}:00`
  if (!h) return `${hh} — nothing recorded`
  const bits: string[] = []
  if (h.cloud_seconds) bits.push(`${Math.round(h.cloud_seconds / 60)} min in storage`)
  if (h.node_seconds) bits.push(`${Math.round(h.node_seconds / 60)} min on the node`)
  return bits.length ? `${hh} — ${bits.join(', ')}` : `${hh} — nothing recorded`
}

export function DayTimeline({
  date, hours, clips, uploadsAudio, observedUtc, onOpenClip,
}: {
  date: string
  hours: DayHour[]
  clips: DayClip[]
  uploadsAudio: boolean
  observedUtc: string | null
  onOpenClip: (clip: DayClip) => void
}) {
  const byHour = new Map(hours.map((h) => [h.h, h]))
  const anything = hours.some((h) => h.node_seconds || h.cloud_seconds) || clips.length > 0

  return (
    <div className="tl">
      <div className="tl-bar" role="img"
           aria-label={`Availability for ${date}, hour by hour`}>
        {Array.from({ length: 24 }, (_, hour) => {
          const h = byHour.get(hour)
          // Proportional, never rounded to a full block. The oldest hour on a
          // card is normally half-eaten by the janitor and the current hour is
          // still filling; both are the steady state, and squaring them off
          // would misreport what is actually held.
          const cloud = h ? h.cloud_seconds / HOUR : 0
          const node = h ? h.node_seconds / HOUR : 0
          return (
            <div key={hour} className="tl-hour" title={label(h, hour)}>
              <span className="tl-gap" />
              {node > 0 && <span className="tl-node" style={{ height: pct(node) }} />}
              {cloud > 0 && <span className="tl-cloud" style={{ height: pct(cloud) }} />}
            </div>
          )
        })}

        {/* Markers sit above the bar, positioned by time of day. A clip is
            seconds long against a 24-hour axis, so it is a mark rather than a
            band -- drawing it to scale would make it invisible. */}
        {clips.map((c) => {
          // Positioned and labelled on the same clock. It used to be positioned
          // by getUTCHours() and labelled by fmtTime(), which is local -- so in
          // Norway the mark sat two hours from where its own tooltip said it
          // was, and neither number was wrong on its own.
          const frac = hourFraction(new Date(c.started_utc).getTime() / 1000)
          return (
            <button
              key={c.clip_id}
              className="tl-clip"
              style={{ left: pct(frac) }}
              onClick={() => onOpenClip(c)}
              /* The capture gain only for a clip that was captured. A mark
                 points at a recording the node made, so there is no gain this
                 clip was taken through and the sentence simply ends earlier. */
              aria-label={`Clip at ${fmtTime(c.started_utc)}, ${c.duration_s.toFixed(1)} seconds`
                + (c.gain_db == null ? ''
                  : `, captured at ${c.gain_db >= 0 ? '+' : '−'}${Math.abs(c.gain_db).toFixed(1)} dB`)}
              title={`${fmtTime(c.started_utc)} · ${c.duration_s.toFixed(1)} s`}
            />
          )
        })}
      </div>

      {/* Every third hour. All 24 overlap below about 500px, and a label that
          collides with its neighbour is worse than one that is absent. */}
      <div className="tl-axis" aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => (
          <span key={i} style={{ left: pct((i * 3) / 24) }}>
            {String(i * 3).padStart(2, '0')}
          </span>
        ))}
      </div>

      <p className="faint tl-key">
        <i className="sw cloud" /> in storage
        <i className="sw node" /> on the node
        <i className="sw gap" /> gap
        {clips.length > 0 && <><i className="sw clip" /> clip</>}
      </p>

      {!anything && (
        <p className="faint" style={{ margin: '2px' }}>
          Nothing recorded on {date}, and nothing held on the node for it.
        </p>
      )}

      {!uploadsAudio && hours.some((h) => h.node_seconds) && (
        <p className="faint" style={{ margin: '2px', fontSize: 12 }}>
          This node keeps audio on its card by design — its tier uploads spectra
          rather than sound, so "on the node" is the intended resting place and
          not a backlog. Saved clips are the audio that reaches storage.
        </p>
      )}

      {observedUtc && (
        <p className="faint num" style={{ margin: '2px', fontSize: 12 }}>
          Node last reported {fmtTime(observedUtc)}. What it holds is a snapshot
          from then, not a live reading.
        </p>
      )}
    </div>
  )
}
